import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import {
  FALLBACK_TIMEZONE,
  formatDay,
  formatTableTime,
  formatTimestamp,
  rawTimestampTitle,
} from './format';

/**
 * The dashboard timezone is a runtime value (functional spec, API Contract Reference — Runtime configuration), so it has to reach the
 * formatting helpers from React rather than from a module constant. A context carries it
 * and `useTimeFormat()` binds the pure helpers in `format.ts` to it; nothing writes a
 * module-level variable, so a component rendering before the config resolves simply sees
 * the fallback and re-renders once it arrives.
 */
const TimezoneContext = createContext<string>(FALLBACK_TIMEZONE);

export function TimezoneProvider({
  timezone,
  children,
}: {
  timezone: string;
  children: ReactNode;
}) {
  return <TimezoneContext.Provider value={timezone}>{children}</TimezoneContext.Provider>;
}

/** The zone currently in force — `America/Toronto` until `/admin/api/config` answers. */
export function useTimezone(): string {
  return useContext(TimezoneContext);
}

export interface TimeFormatters {
  timezone: string;
  formatTimestamp: (iso: string | null | undefined) => string;
  formatTableTime: (iso: string | null | undefined) => string;
  formatDay: (iso: string | null | undefined) => string;
  rawTimestampTitle: (iso: string | null | undefined) => string;
}

/** Timestamp helpers bound to the zone in force. */
export function useTimeFormat(): TimeFormatters {
  const timezone = useTimezone();
  return useMemo(
    () => ({
      timezone,
      formatTimestamp: (iso) => formatTimestamp(iso, timezone),
      formatTableTime: (iso) => formatTableTime(iso, timezone),
      formatDay: (iso) => formatDay(iso, timezone),
      rawTimestampTitle: (iso) => rawTimestampTitle(iso, timezone),
    }),
    [timezone],
  );
}
